import { useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { AnalyzeTinybirdResourceResponseBodyType } from "@intelligems/ig-types/functions/analytics/analyze-resource-tinybird/analyze-resource-tinybird";
import { GraphConfig } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import { STANDARD_EVENTS_ALLOWED_KEY_METRICS } from "@intelligems/ig-types/dtos/analytics-events/standard-events";
import {
  shouldShowProfitData,
  shouldShowProfitPlaceholder,
} from "@src/features/analytics/utils";
import { MobileAnalyticsFrame } from "@src/features/mobileAnalytics/shared/components/MobileAnalyticsFrame";
import { MobileStatSigSection } from "@src/features/mobileAnalytics/testDetails/components/MobileStatSigSection";
import { useGenerateGraphConfig } from "@src/features/mobileAnalytics/testDetails/hooks/useGenerateGraphConfig";
import { getExperienceAnalyticsFilters } from "@src/store/slices/dashboardFilters";
import { selectedVariations } from "@src/store/slices/variations";
import { UseQueryResult } from "@tanstack/react-query";

const isProfitRelatedStandardMetric = (metricId: string): boolean => {
  for (const [key, value] of Object.entries(
    STANDARD_EVENTS_ALLOWED_KEY_METRICS
  )) {
    if (
      value.analyticsMetricId === metricId &&
      key.toLowerCase().includes("profit")
    ) {
      return true;
    }
  }
  return false;
};

interface MobileStatisticalSignificanceProps {
  variationOverviewData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  graphConfigs: GraphConfig[];
}

export const MobileStatisticalSignificance = ({
  variationOverviewData,
  graphConfigs,
}: MobileStatisticalSignificanceProps) => {
  const dashboardFilters = useSelector(getExperienceAnalyticsFilters);
  const variations = useSelector(selectedVariations);

  const selectedBaseline = variations.find(
    (v) => v.id === dashboardFilters.compareGroupA
  );
  const generatedOverviewGraphConfig = useGenerateGraphConfig();

  // Generate dropdown options for metric selection
  const dropdownOptions = generatedOverviewGraphConfig.map((config) => ({
    title: config.title,
    id: config.impactMetric || config.metrics[0] || "",
  }));

  const shouldShowProfit = shouldShowProfitData(
    variationOverviewData[0]?.data?.metrics || []
  );

  const filteredDropdownOptions =
    shouldShowProfit === true
      ? dropdownOptions
      : dropdownOptions.filter(
          (option) => !isProfitRelatedStandardMetric(option.id)
        );

  const filteredConfigs = graphConfigs.filter((config) => {
    return !shouldShowProfitPlaceholder(shouldShowProfit, config);
  });

  const statSigConfigs = filteredConfigs.filter(
    (config) => config.hasValueCI || config.hasProbability
  );

  const getInitialId = () => {
    const firstStatSigConfig = statSigConfigs[0];
    if (!firstStatSigConfig) {
      return filteredDropdownOptions[0]?.id || "";
    }
    return (
      firstStatSigConfig.impactMetric || firstStatSigConfig.metrics[0] || ""
    );
  };

  const [selectedMetricId, setSelectedMetricId] =
    useImmer<string>(getInitialId());

  const selectedConfig =
    filteredConfigs.find(
      (c) =>
        c.impactMetric === selectedMetricId || c.metrics[0] === selectedMetricId
    ) || statSigConfigs[0];

  if (!statSigConfigs.length || !selectedConfig) {
    return null;
  }

  return (
    <MobileAnalyticsFrame
      title="Statistical Significance"
      selectedValue={selectedMetricId}
      onSelectionChange={setSelectedMetricId}
      dropdownOptions={filteredDropdownOptions}
    >
      <MobileStatSigSection
        selectedMetricId={selectedMetricId}
        graphConfigs={statSigConfigs}
        variationOverviewData={variationOverviewData}
        controlGroupName={selectedBaseline?.name || "Default"}
        selectedConfig={selectedConfig}
      />
    </MobileAnalyticsFrame>
  );
};
